import React from 'react';
import { motion } from 'framer-motion';
import { Instagram, Mail, MessageCircle } from 'lucide-react';
const socials = [{
  icon: MessageCircle,
  label: 'WhatsApp',
  href: 'https://wa.me/5491122836110'
}, {
  icon: Instagram,
  label: 'Instagram',
  href: 'https://instagram.com/buceoynavegacion'
}, {
  icon: Mail,
  label: 'Email',
  href: 'mailto:buceoynavegacion@gmail.com'
}];
export function Hero() {
  return <section className="relative h-screen min-h-[640px] w-full overflow-hidden bg-zinc-950 text-white" data-id="element-33" data-mp="127eeddqu7pdg">
      {/* Cinematic background */}
      <div className="absolute inset-0 w-full h-full" data-id="element-34" data-mp="saruar6c52ch">
        <video autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover" poster="https://images.unsplash.com/photo-1682687982501-1e58f8147228?q=80&w=2070&auto=format&fit=crop" data-id="element-35" data-mp="sde28rx51jv8">
          <source src="https://cdn.pixabay.com/video/2020/05/12/38874-421040854_large.mp4" type="video/mp4" data-id="element-36" data-mp="381nqyygt48yo" />
        </video>
        {/* Dark overlay 60–75% + subtle vignette for premium depth */}
        <div className="absolute inset-0 bg-zinc-950/70" data-id="element-37" data-mp="3o59dnnvztegn" />
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/60 via-transparent to-zinc-950/80" data-id="element-38" data-mp="399jmlbbyab52" />
      </div>

      {/* Foreground layout */}
      <div className="relative z-20 flex h-full flex-col px-6 py-8 md:px-12 md:py-10" data-id="element-39" data-mp="30i1abbi4kfh9">
        {/* Top bar */}
        <header className="flex items-start justify-between gap-6" data-id="element-40" data-mp="358hoixtrs54o">
          {/* Brand block — top left */}
          <motion.div initial={{
          opacity: 0,
          y: -12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} data-id="element-41" data-mp="2qkda461r2frp">
            <h2 className="font-heading text-xl md:text-2xl font-semibold uppercase leading-tight tracking-[0.12em]" data-id="element-42" data-mp="2ll0fgz4n04ek">
              Buceo y<br data-id="element-43" data-mp="s3mcu1g2bwxu" />
              Navegación
            </h2>
            <div className="mt-3 font-mono text-[10px] md:text-xs uppercase leading-relaxed tracking-[0.25em] text-blue-200/80" data-id="element-44" data-mp="nat6j94y4gjn">
              <p data-id="element-45" data-mp="id03uq2uq4o4">Escuela de Buceo</p>
              <p data-id="element-46" data-mp="i9vpizvo9sjq">Agencia de Turismo HC 26.614</p>
            </div>
          </motion.div>

          {/* Social icons — top right */}
          <motion.nav aria-label="Redes sociales" initial={{
          opacity: 0,
          y: -12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.1,
          ease: 'easeOut'
        }} className="flex items-center gap-3" data-id="element-47" data-mp="1b1fez1iv42u6">
            {socials.map(({
            icon: Icon,
            label,
            href
          }) => <a key={label} href={href} target={href.startsWith('http') ? '_blank' : undefined} rel={href.startsWith('http') ? 'noopener noreferrer' : undefined} aria-label={label} className="group flex h-11 w-11 items-center justify-center rounded-full border border-white/30 text-white/90 transition-all duration-300 hover:scale-105 hover:border-blue-400 hover:bg-white/5 hover:text-white md:h-12 md:w-12" data-id="element-48" data-mp="3hrlm2muo2fb3">
                <Icon className="h-4 w-4 md:h-[18px] md:w-[18px]" strokeWidth={1.5} data-id="element-49" data-mp="2fzarzh50i4ct" />
              </a>)}
          </motion.nav>
        </header>

        {/* Center content */}
        <div className="flex flex-1 flex-col items-center justify-center text-center" data-id="element-50" data-mp="2tzlj8la8hkyi">
          <motion.h1 initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.9,
          delay: 0.25,
          ease: 'easeOut'
        }} className="font-heading" data-id="element-51" data-mp="2tx7ka1cfnolo">
            <span className="block text-base font-medium uppercase tracking-[0.4em] text-blue-100/80 md:text-xl" data-id="element-52" data-mp="3vh5ju6cof677">
              Exploramos el mundo
            </span>
            <span className="mt-2 block text-6xl font-bold uppercase leading-[0.95] tracking-tight sm:text-7xl md:text-8xl lg:text-9xl" data-id="element-53" data-mp="9a7e7jclsku2">
              Bajo el agua
            </span>
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.9,
          delay: 0.45,
          ease: 'easeOut'
        }} className="mt-8 max-w-xl text-base font-light text-zinc-300 md:text-lg" data-id="element-54" data-mp="3cs8r46wxglr3">
            Viajes de buceo y formación profesional desde hace más de 10 años.
          </motion.p>

          <motion.a href="#adventures" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.9,
          delay: 0.6,
          ease: 'easeOut'
        }} className="group relative mt-12 inline-flex items-center justify-center overflow-hidden rounded-full border border-white/40 px-10 py-4 text-xs font-medium uppercase tracking-[0.3em] text-white transition-all duration-300 hover:border-blue-400" data-id="element-55" data-mp="19hvpqyj577d1">
            <span className="absolute inset-0 translate-y-full bg-blue-600 transition-transform duration-500 ease-out group-hover:translate-y-0" data-id="element-56" data-mp="1j8747f0gcyy6" />
            <span className="relative z-10" data-id="element-57" data-mp="7ss8eejzcd4n">Explorar destinos</span>
          </motion.a>

          {/* Destination preview line */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 1,
          delay: 0.9,
          ease: 'easeOut'
        }} className="mt-10 flex items-center gap-3 text-[11px] uppercase tracking-[0.35em] text-white/60 md:text-xs" data-id="element-58" data-mp="bvoi1frjkywg">
            <a href="https://bonaire.buceoynavegacion.com/" target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-white" data-id="element-59" data-mp="1zs4rm2ycqy04">
              Bonaire
            </a>
            <span className="text-blue-400/70" data-id="element-60" data-mp="1lsd6zjdbyjd5">·</span>
            <a href="https://fernandodenoronha2026.buceoynavegacion.com/" target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-white" data-id="element-61" data-mp="1lt52yvauaj3y">
              Noronha
            </a>
            <span className="text-blue-400/70" data-id="element-62" data-mp="3b8w2ctdmtjcm">·</span>
            <a href="#courses" className="transition-colors hover:text-white" data-id="element-63" data-mp="3jotoyxgh1e68">
              Cursos
            </a>
          </motion.div>
        </div>
      </div>
    </section>;
}